import { ChevronLeft, LogOut, RefreshCw, User } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCanGoBack } from "@/hooks/useCanGoBack";
import { brand } from "@/lib/branding";

interface MobileHeaderProps {
  onLogout: () => void;
  isSigningOut: boolean;
  onNavigateToProfile: () => void;
  userRole: "admin" | "user";
  userName?: string;
  etablissementNom?: string;
}

// Mapping pathname -> titre affiche dans le header mobile
// Les pages detail (`/chantiers/:id`) heritent du label parent.
const TITLE_MAP: Record<string, string> = {
  "/": "Tableau de bord",
  "/chantiers": "Mes chantiers",
  "/transports": "Mes transports",
  "/cap": "Mes Déchets",
  "/devis-factures": "Devis & Factures",
  "/demandes": "Mes demandes",
  "/documents": "Documents",
  "/documents-admin": "Documents",
  "/mes-entreprises": "Mes entreprises",
  "/mon-etablissement": "Mon etablissement",
  "/profil": "Mon profil",
  "/operations": "Operations",
  "/etablissements": "Etablissements",
  "/prix": "Prix",
  "/types-categories": "Types & Categories",
};

function resolveTitle(pathname: string): string {
  if (TITLE_MAP[pathname]) return TITLE_MAP[pathname];
  // pages detail : `/chantiers/abc` -> retourne le label de `/chantiers`
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length >= 1) {
    const parent = `/${segments[0]}`;
    if (TITLE_MAP[parent]) return TITLE_MAP[parent];
  }
  return brand.name;
}

export function MobileHeader({
  onLogout,
  isSigningOut,
  onNavigateToProfile,
  userRole,
  userName,
  etablissementNom,
}: MobileHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const title = resolveTitle(location.pathname);
  const showBack = useCanGoBack();

  const userLabel =
    userRole === "admin"
      ? `Admin ${brand.name}`
      : userName && etablissementNom
        ? `${userName} (${etablissementNom})`
        : userName || "Utilisateur";

  return (
    <header className="relative flex items-center justify-between px-3 h-14 box-content pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] bg-header sticky top-0 z-50">
      <div className="flex items-center shrink-0 z-10">
        {showBack ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-header-foreground hover:bg-white/10"
            aria-label="Retour"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        ) : (
          <img src={brand.logoUrl} alt={brand.name} className="h-8 w-auto" />
        )}
      </div>

      <span
        className="absolute inset-x-0 flex items-center justify-center text-header-foreground font-medium pointer-events-none px-24"
      >
        <span className="truncate">{title}</span>
      </span>

      <div className="flex items-center gap-0.5 shrink-0 z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => window.location.reload()}
          className="text-header-foreground hover:bg-white/10"
          aria-label="Actualiser"
          title="Actualiser"
        >
          <RefreshCw className="h-5 w-5" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-header-foreground hover:bg-white/10"
              aria-label="Menu utilisateur"
            >
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{userLabel}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onNavigateToProfile}>
              <User className="h-4 w-4 mr-2" />
              Mon profil
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} disabled={isSigningOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
